import { Dialog, DialogPanel } from "@headlessui/react";
import clsx from "clsx";
import { ReactNode } from "react";

type ModalPropTypes = {
  actions?: ReactNode[];
  actionsAlign?: "between" | "center" | "end" | "start";
  children: JSX.Element;
  isOpen: boolean;
  noClose?: boolean;
  onClose: () => void;
  subtitle?: ReactNode | string;
  title?: ReactNode | string;
  width?: number;
  zIndex?: number;
};

export default function Modal({
  actions,
  actionsAlign = "start",
  children,
  isOpen,
  noClose,
  onClose,
  subtitle,
  title,
  width = 700,
  zIndex,
}: ModalPropTypes) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative"
      style={{ zIndex: zIndex ?? 50 }}
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-2 lg:p-3">
          <DialogPanel
            className="relative h-full w-full md:h-auto"
            style={{ maxWidth: width }}
          >
            <div className="relative rounded-lg bg-white shadow-md dark:bg-stone-800 p-2 lg:p-3">
              <div>
                <div
                  className={clsx(
                    "flex items-start justify-between rounded-t p-1.5 lg:p-2",
                    {
                      "dark:border-gray-700": !noClose && (title || subtitle),
                    },
                  )}
                >
                  <div>
                    {title && (
                      <h3 className="text-base lg:text-lg xl:text-xl font-semibold text-gray-900 dark:text-white">
                        {title}
                      </h3>
                    )}
                    {subtitle && (
                      <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-300 mt-1.5 lg:mt-2">
                        {subtitle}
                      </p>
                    )}
                  </div>
                  <div className="ml-auto flex items-center gap-1">
                    {!noClose && (
                      <button
                        type="button"
                        onClick={() => onClose()}
                        className="inline-flex items-center rounded-lg bg-transparent p-1 lg:p-1.5 text-xs lg:text-sm text-black hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
                        data-modal-toggle="defaultModal"
                      >
                        <svg
                          aria-hidden="true"
                          className="h-4 w-4 lg:h-5 lg:w-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                        <span className="sr-only">Close modal</span>
                      </button>
                    )}
                  </div>
                </div>
                {children}
              </div>
              {actions && actions?.length > 0 && (
                <div
                  className={clsx(
                    "flex gap-2 lg:gap-3 py-1.5 lg:py-2 px-1.5 lg:px-2",
                    {
                      "justify-between": actionsAlign === "between",
                      "justify-center": actionsAlign === "center",
                      "justify-end": actionsAlign === "end",
                      "justify-start": actionsAlign === "start",
                    },
                  )}
                >
                  {actions?.map((item, i) => (
                    <span key={i}>{item}</span>
                  ))}
                </div>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
